import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { Store } from '../../../../../Store';
import { getClient, getProgramme, getCohorts, getAgents, updateCohort, getCohort, generateCohortDates } from '../../../../../api';
import { sortNameAlphabetically, dateTimeToUTC } from '../../../../../helpers';

import AdminContainer from '../../../AdminContainer/AdminContainer';
import AdminInfoBar from '../../../AdminInfoBar/AdminInfoBar';
import AdminTop from '../../../AdminTop/AdminTop';
import AdminCard, { AdminCardBody, AdminCardNumber } from '../../../AdminCard/AdminCard';
import Input, { InputRow, InputGroup } from '../../../../Input/Input';
import AdminActionBar from '../../../AdminActionBar/AdminActionBar';
import UploadLeadersFormModal from '../../../UploadLeadersFormModal/UploadLeadersFormModal';
import ErrorMessage from '../../../../ErrorMessage/ErrorMessage';
import SuccessMessage from '../../../../SuccessMessage/SuccessMessage';
import AdminLeavingPageModal from '../../../AdminLeavingPageModal/AdminLeavingPageModal';

import UploadIcon from '../../../../Icons/UploadIcon';
import PlusIcon from '../../../../Icons/PlusIcon';
import BellIcon from '../../../../Icons/BellIcon';

import './EditCohort.scss';

function EditCohortCard({cohort, number, agents, urlPrepend, setFormChange}) {
    const { state } = useContext(Store);
    const { times, defaultTime, uploadLeaderTemplateLink } = state;

    const {clientId, programmeId, cohortId} = useParams();

    const [formLoading, setFormLoading] = useState(false);
    const [formErrors, setFormErrors] = useState(false);
    const [formSuccess, setFormSuccess] = useState(false);
    const [addLeadersSuccess, setAddLeadersSuccess] = useState(false);

    const [nameValue, setNameValue] = useState(cohort?.name ?? '');
    const [agentValue, setAgentValue] = useState(cohort?.agentId ?? '');
    const [nominationInviteDateValue, setNominationInviteDateValue] = useState(cohort?.nominationInviteDate ? utcToZonedTime(parseISO(cohort?.nominationInviteDate), 'UTC') : '');
    const [nominationInviteTimeValue, setNominationInviteTimeValue] = useState(cohort?.nominationInviteDate ? format(utcToZonedTime(parseISO(cohort?.nominationInviteDate), 'UTC'), 'HH:mm') : defaultTime);
    const [nominationReminder1DateValue, setNominationReminder1DateValue] = useState(cohort?.nominationsReminder1 ? utcToZonedTime(parseISO(cohort?.nominationsReminder1), 'UTC') : '');
    const [nominationReminder1TimeValue, setNominationReminder1TimeValue] = useState(cohort?.nominationsReminder1 ? format(utcToZonedTime(parseISO(cohort?.nominationsReminder1), 'UTC'), 'HH:mm') : defaultTime);
    const [nominationsDeadlineDateValue, setNominationsDeadlineDateValue] = useState(cohort?.nominationsDeadline ? utcToZonedTime(parseISO(cohort?.nominationsDeadline), 'UTC') : '');
    const [nominationsDeadlineTimeValue, setNominationsDeadlineTimeValue] = useState(cohort?.nominationsDeadline ? format(utcToZonedTime(parseISO(cohort?.nominationsDeadline), 'UTC'), 'HH:mm') : defaultTime);
    const [surveyReminder1DateValue, setSurveyReminder1DateValue] = useState(cohort?.surveyReminder1 ? utcToZonedTime(parseISO(cohort?.surveyReminder1), 'UTC') : '');
    const [surveyReminder1TimeValue, setSurveyReminder1TimeValue] = useState(cohort?.surveyReminder1 ? format(utcToZonedTime(parseISO(cohort?.surveyReminder1), 'UTC'), 'HH:mm') : defaultTime);
    const [surveyReminder2DateValue, setSurveyReminder2DateValue] = useState(cohort?.surveyReminder2 ? utcToZonedTime(parseISO(cohort?.surveyReminder2), 'UTC') : '');
    const [surveyReminder2TimeValue, setSurveyReminder2TimeValue] = useState(cohort?.surveyReminder2 ? format(utcToZonedTime(parseISO(cohort?.surveyReminder2), 'UTC'), 'HH:mm') : defaultTime);
    const [surveyDeadlineDateValue, setSurveyDeadlineDateValue] = useState(cohort?.surveyDeadline ? utcToZonedTime(parseISO(cohort?.surveyDeadline), 'UTC') : '');
    const [surveyDeadlineTimeValue, setSurveyDeadlineTimeValue] = useState(cohort?.surveyDeadline ? format(utcToZonedTime(parseISO(cohort?.surveyDeadline), 'UTC'), 'HH:mm') : defaultTime);

    const [nameError, setNameError] = useState(false);
    const [agentError, setAgentError] = useState(false);
    const [nominationInviteDateError, setNominationInviteDateError] = useState(false);
    const [nominationReminder1DateError, setNominationReminder1DateError] = useState(false);
    const [nominationsDeadlineDateError, setNominationsDeadlineDateError] = useState(false);
    const [surveyReminder1DateError, setSurveyReminder1DateError] = useState(false);
    const [surveyReminder2DateError, setSurveyReminder2DateError] = useState(false);
    const [surveyDeadlineDateError, setSurveyDeadlineDateError] = useState(false);

    const [showUploadLeadersModal, setShowUploadLeadersModal] = useState(false);

    const onSubmitEditCohortForm = e => {
        e.preventDefault();

        setFormLoading(true);

        setFormErrors(false);
        setNameError(false);
        setAgentError(false);
        setNominationInviteDateError(false);
        setNominationReminder1DateError(false);
        setNominationsDeadlineDateError(false);
        setSurveyReminder1DateError(false);
        setSurveyReminder2DateError(false);
        setSurveyDeadlineDateError(false);
        setFormSuccess(false);

        updateCohort(clientId, programmeId, cohort.id, {
            name: nameValue,
            agentId: agentValue,
            nominationInviteDate: dateTimeToUTC(nominationInviteDateValue, nominationInviteTimeValue),
            nominationsDeadline: dateTimeToUTC(nominationsDeadlineDateValue, nominationsDeadlineTimeValue),
            surveyDeadline: dateTimeToUTC(surveyDeadlineDateValue, surveyDeadlineTimeValue),
            nominationsReminder1: dateTimeToUTC(nominationReminder1DateValue, nominationReminder1TimeValue),
            surveyReminder1: dateTimeToUTC(surveyReminder1DateValue, surveyReminder1TimeValue),
            surveyReminder2: dateTimeToUTC(surveyReminder2DateValue, surveyReminder2TimeValue)
        }).then(response => {
            if (response?.success) {
                setFormSuccess('Your changes have been saved.');
                setFormChange(false);
            }
            
            if (response?.errors) {
                setNameError(response.errors?.Name ?? false);
                setAgentError(response.errors?.AgentId ?? false);
                setNominationInviteDateError(response.errors?.NominationInviteDate ?? false);
                setNominationReminder1DateError(response.errors?.NominationsReminder1 ?? false);
                setNominationsDeadlineDateError(response.errors?.NominationsDeadline ?? false);
                setSurveyReminder1DateError(response.errors?.SurveyReminder1 ?? false);
                setSurveyReminder2DateError(response.errors?.SurveyReminder2 ?? false);
                setSurveyDeadlineDateError(response.errors?.SurveyDeadline ?? false);

                let errors = Object.assign({}, response.errors);

                delete errors.Name;
                delete errors.AgentId;
                delete errors.NominationInviteDate;
                delete errors.NominationsReminder1;
                delete errors.NominationsDeadline;
                delete errors.SurveyReminder1;
                delete errors.SurveyReminder2;
                delete errors.SurveyDeadline;

                setFormErrors(errors);
            }

            setFormLoading(false);
        }).catch(() => {
            setFormLoading(false);
        });
    };

    const onClickGenerateDatesBtn = () => {
        setFormLoading(true);
        setNominationInviteDateError(false);
        setSurveyDeadlineDateError(false);

        if (!dateTimeToUTC(nominationInviteDateValue, nominationInviteTimeValue)) {
            setNominationInviteDateError(['Nomination deadline date is required']);
        }

        if (!dateTimeToUTC(surveyDeadlineDateValue, surveyDeadlineTimeValue)) {
            setSurveyDeadlineDateError(['Survey deadline date is required']);
        }

        if (!dateTimeToUTC(nominationInviteDateValue, nominationInviteTimeValue) || !dateTimeToUTC(surveyDeadlineDateValue, surveyDeadlineTimeValue)) {
            setFormLoading(false);
            return false;
        }

        generateCohortDates(dateTimeToUTC(nominationInviteDateValue, nominationInviteTimeValue), dateTimeToUTC(surveyDeadlineDateValue, surveyDeadlineTimeValue))
            .then(response => {
                setNominationReminder1DateValue(response?.nominationsReminder1 ? utcToZonedTime(parseISO(response.nominationsReminder1), 'UTC') : null);
                setNominationReminder1TimeValue(response?.nominationsReminder1 ? format(utcToZonedTime(parseISO(response.nominationsReminder1), 'UTC'), 'HH:mm') : null);
                setNominationsDeadlineDateValue(response?.nominationsDeadline ? utcToZonedTime(parseISO(response.nominationsDeadline), 'UTC') : null);
                setNominationsDeadlineTimeValue(response?.nominationsDeadline ? format(utcToZonedTime(parseISO(response.nominationsDeadline), 'UTC'), 'HH:mm') : null);

                setSurveyReminder1DateValue(response?.surveyReminder1 ? utcToZonedTime(parseISO(response.surveyReminder1), 'UTC') : null);
                setSurveyReminder1TimeValue(response?.surveyReminder1 ? format(utcToZonedTime(parseISO(response.surveyReminder1), 'UTC'), 'HH:mm') : null);
                setSurveyReminder2DateValue(response?.surveyReminder2 ? utcToZonedTime(parseISO(response.surveyReminder2), 'UTC') : null);
                setSurveyReminder2TimeValue(response?.surveyReminder2 ? format(utcToZonedTime(parseISO(response.surveyReminder2), 'UTC'), 'HH:mm') : null);

                setFormLoading(false);
                setFormChange(true);
            });
    };

    const onClickUploadFileBtn = () => {
        setShowUploadLeadersModal(true);
    };

    const onSubmitUploadLeadersFormSuccess = () => {
        setAddLeadersSuccess('Your leaders have been added. These will now appear in your cohort list.');
        setShowUploadLeadersModal(false);
    };

    const onChangeNameInput = e => {
        setNameValue(e.target.value);
        setFormChange(true);
    };

    const onChangeAgentSelect = e => {
        setAgentValue(e.target.value);
        setFormChange(true);
    };

    const onChangeNominationInviteDateInput = date => {
        setNominationInviteDateValue(date);
        setFormChange(true);
    };

    const onChangeNominationInviteTimeInput = e => {
        setNominationInviteTimeValue(e.target.value);
        setFormChange(true);
    };

    const onChangeNominationReminder1DateInput = date => {
        setNominationReminder1DateValue(date);
        setFormChange(true);
    };

    const onChangeNominationReminder1TimeInput = e => {
        setNominationReminder1TimeValue(e.target.value);
        setFormChange(true);
    };

    const onChangeNominationsDeadlineDateInput = date => {
        setNominationsDeadlineDateValue(date);
        setFormChange(true);
    };

    const onChangeNominationsDeadlineTimeInput = e => {
        setNominationsDeadlineTimeValue(e.target.value);
        setFormChange(true);
    };

    const onChangeSurveyReminder1DateInput = date => {
        setSurveyReminder1DateValue(date);
        setFormChange(true);
    };

    const onChangeSurveyReminder1TimeInput = e => {
        setSurveyReminder1TimeValue(e.target.value);
        setFormChange(true);
    };

    const onChangeSurveyReminder2DateInput = date => {
        setSurveyReminder2DateValue(date);
        setFormChange(true);
    };

    const onChangeSurveyReminder2TimeInput = e => {
        setSurveyReminder2TimeValue(e.target.value);
        setFormChange(true);
    };

    const onChangeSurveyDeadlineDateInput = date => {
        setSurveyDeadlineDateValue(date);
        setFormChange(true);
    };

    const onChangeSurveyDeadlineTimeInput = e => {
        setSurveyDeadlineTimeValue(e.target.value);
        setFormChange(true);
    };

    return (
        <AdminCard>
            <AdminCardNumber>
                {number}
            </AdminCardNumber>
            <AdminCardBody>
                { formErrors ? <ErrorMessage message={formErrors} /> : null }

                { formSuccess ? <SuccessMessage message={formSuccess} setFormSuccess={setFormSuccess} /> : null }

                <form className="add-new-cohort-form" onSubmit={onSubmitEditCohortForm}>
                    { formLoading ? <div className="loading-circle"></div> : null }

                    <div className={`add-new-cohort-form-inner ${formLoading ? 'loading' : ''}`}>
                        <InputRow>
                            <Input type="text" label="Cohort Name*" name="name" value={nameValue} placeholder="Enter cohort name" errors={nameError} onChange={onChangeNameInput} />
                            
                            <Input type="select" 
                                label="Assign a Holos agent for this cohort*" 
                                name="agent" 
                                selectText="Holos Agent" 
                                value={agentValue} 
                                options={agents?.users?.sort(sortNameAlphabetically).map(agent => {
                                    return {
                                        value: agent.id,
                                        name: agent.name
                                    }
                                } ) } 
                                loading={!agents?.users} 
                                errors={agentError} 
                                onChange={onChangeAgentSelect} />
                        </InputRow>

                        <InputRow>
                            <InputGroup className="date-time-input-group" label="Nomination Invite*" errors={nominationInviteDateError}>
                                <Input type="datepicker" name="nomination-invite-date" size="small" value={nominationInviteDateValue} placeholder="Enter date" onChange={onChangeNominationInviteDateInput} />
                                
                                <Input name="nomination-invite-time" 
                                    type="select" 
                                    value={nominationInviteTimeValue} 
                                    options={ times.map( time => {
                                        return {
                                            value: time.value,
                                            name: time.text
                                        }
                                    } ) } 
                                    size="extra-small" 
                                    onChange={onChangeNominationInviteTimeInput} />
                            </InputGroup>
                            
                            <InputGroup className="date-time-input-group" label="Survey Deadline*" errors={surveyDeadlineDateError}>
                                <Input type="datepicker" name="survey-deadline-date" size="small" value={surveyDeadlineDateValue} placeholder="Enter date" onChange={onChangeSurveyDeadlineDateInput} />
                                
                                <Input name="survey-deadline-time" 
                                    type="select" 
                                    value={surveyDeadlineTimeValue} 
                                    options={times.map(time => {
                                        return {
                                            value: time.value,
                                            name: time.text
                                        }
                                    } ) } 
                                    size="extra-small" 
                                    onChange={onChangeSurveyDeadlineTimeInput} />
                            </InputGroup>

                            <div className="align-bottom"><button className="btn blue small" type="button" onClick={onClickGenerateDatesBtn}>Re-Generate Dates</button></div>
                        </InputRow>

                        <div className="add-cohort-reminder-dates">

                            <h3 className="sub-header"><div className="icon"><BellIcon /></div> Automated Email Reminders:</h3>

                            <div className="add-cohort-reminder-dates-inner">
                                <div className="add-cohort-reminder-dates-inputs">
                                    <InputRow>
                                        <InputGroup className="date-time-input-group" label="Nomination Reminder 1" errors={nominationReminder1DateError}>
                                            <Input type="datepicker" name="nomination-reminder-1-date" size="small" value={nominationReminder1DateValue} placeholder="Enter date" onChange={onChangeNominationReminder1DateInput} />
                                            
                                            <Input name="nomination-reminder-1-time" 
                                                type="select" 
                                                value={nominationReminder1TimeValue} 
                                                options={ times.map( time => {
                                                    return {
                                                        value: time.value,
                                                        name: time.text
                                                    }
                                                } ) }
                                                size="extra-small" 
                                                onChange={onChangeNominationReminder1TimeInput} />
                                        </InputGroup>

                                        <InputGroup className="date-time-input-group" label="Nomination Deadline (Nomination Reminder 2)" errors={nominationsDeadlineDateError}>
                                            <Input type="datepicker" name="nominations-deadline-date" size="small" value={nominationsDeadlineDateValue} placeholder="Enter date" onChange={onChangeNominationsDeadlineDateInput} />
                                            
                                            <Input name="nomination-deadline-time" 
                                                type="select" 
                                                value={nominationsDeadlineTimeValue} 
                                                options={times.map(time => {
                                                    return {
                                                        value: time.value,
                                                        name: time.text
                                                    }
                                                } ) } 
                                                size="extra-small" 
                                                onChange={onChangeNominationsDeadlineTimeInput} />
                                        </InputGroup>
                                    </InputRow>

                                    <InputRow>
                                        <InputGroup className="date-time-input-group" label="Survey Reminder 1" errors={surveyReminder1DateError}>
                                            <Input type="datepicker" name="survey-reminder-1-date" size="small" value={surveyReminder1DateValue} placeholder="Enter date" onChange={onChangeSurveyReminder1DateInput} />
                                            
                                            <Input name="survey-reminder-1-time" 
                                                type="select" 
                                                value={surveyReminder1TimeValue} 
                                                options={times.map(time => {
                                                    return {
                                                        value: time.value,
                                                        name: time.text
                                                    }
                                                } ) } 
                                                size="extra-small" 
                                                onChange={onChangeSurveyReminder1TimeInput} />
                                        </InputGroup>

                                        <InputGroup className="date-time-input-group" label="Survey Reminder 2" errors={surveyReminder2DateError}>
                                            <Input type="datepicker" name="survey-reminder-2-date" size="small" value={surveyReminder2DateValue} placeholder="Enter date" onChange={onChangeSurveyReminder2DateInput} />
                                            
                                            <Input name="survey-reminder-2-time" 
                                                type="select" 
                                                value={surveyReminder2TimeValue} 
                                                options={times.map(time => {
                                                    return {
                                                        value: time.value,
                                                        name: time.text
                                                    }
                                                } ) } 
                                                size="extra-small" 
                                                onChange={onChangeSurveyReminder2TimeInput} />
                                        </InputGroup>
                                    </InputRow>
                                </div>

                                <div className="add-cohort-reminder-dates-info">
                                    <div className="add-cohort-reminder-dates-info-inner">
                                        <div>
                                            <div className="icon">i</div>
                                        </div>
                                        The email reminder dates have been generated based on the dates above and will be sent out automatically until nominations and surveys are completed.
                                    </div>
                                </div>
                            </div>
                        </div>

                        { addLeadersSuccess ? <SuccessMessage className="add-leader-success" message={addLeadersSuccess} setFormSuccess={setAddLeadersSuccess} /> : null }

                        <InputRow className="add-new-cohort-btns">
                            { cohortId ?
                                <InputGroup label="Add Leaders:">
                                    <div>
                                        <a className="btn" href={uploadLeaderTemplateLink} target="_blank" rel="noopener noreferrer">Download Template</a>
                                    </div>
                                    <div>
                                        <button className="btn" type="button" onClick={onClickUploadFileBtn}><UploadIcon /><div>Upload File</div></button>
                                    </div>
                                    <div>
                                        <Link className="btn" to={`${urlPrepend}/client/${clientId}/programme/${programmeId}/cohort/${cohortId}/add-leader`}><PlusIcon /><div>Add Manually</div></Link>
                                    </div>
                                </InputGroup>
                            : <div></div> }

                            <InputGroup>
                                <div>
                                    <button type="submit" className="btn blue">Save</button>
                                </div>
                            </InputGroup>
                        </InputRow>
                    </div>
                </form>

                <UploadLeadersFormModal clientId={clientId} programmeId={programmeId} cohortId={cohortId} showUploadLeadersModal={showUploadLeadersModal} setShowUploadLeadersModal={setShowUploadLeadersModal} onSubmitSuccess={onSubmitUploadLeadersFormSuccess} />
            </AdminCardBody>
        </AdminCard>
    );
}

export default function EditCohort({setHeaderBreadcrumbs, urlPrepend, productName}) {
    const { state } = useContext(Store);
    const { surveyTypes } = state;

    const {clientId, programmeId, cohortId} = useParams();

    const [pageError, setPageError] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);

    const [client, setClient] = useState(false);
    const [programme, setProgramme] = useState(false);
    const [agents, setAgents] = useState(false);
    const [cohorts, setCohorts] = useState(false);
    const [cohort, setCohort] = useState(false);

    const [formChange, setFormChange] = useState(false);

    const fetchClient = useCallback(clientId => {
        getClient(clientId).then(response => {
            if (response.success) setClient(response);
        });
    }, []);

    const fetchProgramme = useCallback((clientId, programmeId) => {
        getProgramme(clientId, programmeId).then(response => {
            if (response.success) setProgramme(response);
        });
    }, []);

    const fetchAgents = useCallback(() => {
        getAgents().then(response => {
            if (response.success) setAgents(response);
        });
    }, []);

    const fetchCohorts = useCallback((clientId, programmeId) => {
        getCohorts(clientId, programmeId).then(response => {
            if (response.success) {
                setCohorts(response);
            } else {
                setPageError(response.errors);
            }

            setPageLoading(false);
        })
    }, []);

    const fetchCohort = useCallback((clientId, programmeId, cohortId) => {
        getCohort(clientId, programmeId, cohortId).then(response => {
            if (response.success) setCohort(response);
        })
    }, []);

    useEffect(() => {
        document.title = `Edit Cohort - ${productName ? `${productName} -` : ''} Holos Change`;
    }, [productName]);

    useEffect(() => {
        fetchClient(clientId);
    }, [fetchClient, clientId]);

    useEffect(() => {
        fetchProgramme(clientId, programmeId);
    }, [fetchProgramme, clientId, programmeId]);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);

    useEffect(() => {
        fetchCohorts(clientId, programmeId);
    }, [fetchCohorts, clientId, programmeId]);

    useEffect(() => {
        fetchCohort(clientId, programmeId, cohortId);
    }, [fetchCohort, clientId, programmeId, cohortId]);

    useEffect(() => {
        if (programme && cohort) {
            setHeaderBreadcrumbs([
                {
                    text: 'Programmes',
                    link: `${urlPrepend}/`
                }, {
                    text: programme.name,
                    link: `${urlPrepend}/?clientId=${clientId}&programmeId=${programmeId}`
                }, {
                    text: cohort.name,
                    link: `${urlPrepend}/client/${clientId}/programme/${programmeId}/cohort/${cohortId}`
                }, {
                    text: 'Edit cohort'
                }
            ]);
        }
    }, [programme, cohort, urlPrepend, clientId, programmeId, cohortId, setHeaderBreadcrumbs]);

    return (
        <div>
            { !pageLoading && !pageError && client && programme && cohort ?
                <AdminInfoBar>
                    { client?.name && programme?.name ? 
                        <div>
                            <h3>{client.name}</h3>
                            <div>{programme.name}</div>
                        </div>
                    : null }
                    { cohort?.agentName ?
                        <div>
                            <h3>Holos Programme Lead</h3>
                            <div>{cohort.agentName}</div>
                        </div>
                    : null }
                    { programme?.debriefDate ?
                        <div>
                            <h3>Debrief Start Date</h3>
                            <div>{format(utcToZonedTime(parseISO(programme.debriefDate), 'UTC'), 'dd MMM yy, HH:mm')} <strong>UTC</strong></div>
                        </div>
                    : null }
                    { programme?.surveyType && surveyTypes && surveyTypes.filter(surveyType => surveyType.value === programme.surveyType).length ?
                        <div>
                            <h3>Survey Type</h3>
                            <div>{ surveyTypes.filter(surveyType => surveyType.value === programme.surveyType)[0]?.name }</div>
                        </div>
                    : null }
                </AdminInfoBar>
            : null }

            <AdminContainer pageError={pageError} pageLoading={pageLoading}>
                <AdminTop header="Edit cohort" />

                { cohort ? <EditCohortCard cohort={cohort} number={cohorts.cohorts && cohort ? cohorts.cohorts.map((thisCohort, index) => thisCohort.id === cohortId ? index + 1 : null) : null} agents={agents} urlPrepend={urlPrepend} setFormChange={setFormChange} /> : null }

            </AdminContainer>

            <AdminActionBar>
                <div>
                </div>
                <div>
                    <div><Link className="btn" to={`${urlPrepend}/client/${clientId}/programme/${programmeId}/cohort/${cohortId}`}>Return to Cohort</Link></div>
                </div>
            </AdminActionBar>

            <AdminLeavingPageModal formChange={formChange}>
                <h2>Leaving this page?</h2>
                <p>The cohort has not been saved.</p>
            </AdminLeavingPageModal>
        </div>
    );
}
